/**
 * multidimensional iterable where in first level are brands, and in second their services to be filtered
 */
const Logger = require("../../services/Logger");

type ServicesLists = Array<Set<unknown>> | Array<unknown[]>;

/**
 *  check if item is included at least once in every subArray
 *  check if service that is marked as "filterable" (is not included, is unknown or is mixed) for one brand is also
 *  filterable for other brands
 */
const isItemInEverySubArray = (
  item: unknown,
  array: ServicesLists
): boolean => {
  return array
    .map((subArray) => {
      return [...subArray].includes(item);
    })
    .every((current) => current === true);
};

/**
 * gets multidimensional array (3 levels), of list of services for each brand that are supposed to be filtered
 * merge all "filterable" services to one iterable
 */
const mergeServicesToFilter = (
  filteringData: string[][][]
): Array<Set<string>> | [] => {
  const resultArray: Array<Set<string>> = [];

  try {
    filteringData.forEach((brands, dataIndex) => {
      brands.forEach((brand, brandIndex) => {
        if (dataIndex === 0) {
          resultArray[brandIndex] = new Set(brand);
        } else {
          resultArray[brandIndex] = new Set([
            ...resultArray[brandIndex].values(),
            ...brand,
          ]);
        }
      });
    });
  } catch (e) {
    Logger.error(e);
    throw new Error(`Error in mergeServicesToFilter ${e}`);
  }

  return resultArray;
};

/**
 * Helper function that creates list of services to filter out of potentially filterable services
 */
const listFilteredServices = (filterableServices: ServicesLists): string[] => {
  const filteredServices = [];
  filterableServices[0].forEach((service) => {
    const isServiceUnknownForEveryBrand = isItemInEverySubArray(
      service,
      filterableServices
    );

    if (isServiceUnknownForEveryBrand) {
      filteredServices.push(service);
    }
  });
  return filteredServices;
};

/**
 * gets lists of not included, mixed and unknown services for each brand and creates final list of
 * services that should be filtered.
 * service should be filtered only if is "filterable" (is not included or is unknown or is mixed) for every brand
 */
const createFilteredServicesList = (
  filterUnknownServices: boolean,
  filterNotIncludedServices: boolean,
  filterMixedServices: boolean,
  unknownServices: string[][],
  notIncludedServices: string[][],
  mixedServices: string[][]
): string[] => {
  if (
    filterUnknownServices &&
    filterNotIncludedServices &&
    filterMixedServices
  ) {
    const mergedServices = mergeServicesToFilter([
      mixedServices,
      notIncludedServices,
      unknownServices,
    ]);
    return listFilteredServices(mergedServices);
  }

  if (filterUnknownServices && filterNotIncludedServices) {
    const mergedServices = mergeServicesToFilter([
      notIncludedServices,
      unknownServices,
    ]);
    return listFilteredServices(mergedServices);
  }

  if (filterUnknownServices && filterMixedServices) {
    const mergedServices = mergeServicesToFilter([
      mixedServices,
      unknownServices,
    ]);
    return listFilteredServices(mergedServices);
  }

  if (filterNotIncludedServices && filterMixedServices) {
    const mergedServices = mergeServicesToFilter([
      mixedServices,
      notIncludedServices,
    ]);
    return listFilteredServices(mergedServices);
  }

  if (filterUnknownServices) {
    return listFilteredServices(unknownServices);
  }

  if (filterNotIncludedServices) {
    return listFilteredServices(notIncludedServices);
  }

  if (filterMixedServices) {
    return listFilteredServices(mixedServices);
  }

  return [];
};

export default createFilteredServicesList;
